<template>
  <div>
    <div :class="['block-title', { active: isCurrentBlock, 'sections-open': sectionsOpen }]">
      <div class="toggle-icon-container">
        <FriedIcon icon="chevron-right" class="icon-container" @click.stop="toggleSections" />
      </div>
      <TransitionFadeInOut>
        <div v-if="blockHasChanges && !viewed" class="circle"></div>
      </TransitionFadeInOut>
      <NameInput
        :value="nameInputValue"
        :title="nameInputValue"
        :disabled="true"
        class="block-name"
        :data-external="`dsr-published-navigation-content-id-${block.contentId}-name-input`"
      />
      <FriedIcon v-if="lockedForCc" icon="comments-locked" class="comments-locked-block-title" />
    </div>
    <SectionList
      v-if="sectionsOpen"
      :content-id="block.contentId"
      :sections="visibleSections"
      :participant="participant"
      @navigate-to-section="navigateToSection($event)"
    />
  </div>
</template>
<script lang="ts">
import type { PropType } from 'vue';
import { computed, watch, ref, defineComponent } from 'vue';
import type { EditorBlock } from '@getaccept/editor-lib-new';
import { ContractHelper, PlaceholderHelper } from '@getaccept/editor-lib-new';
import NameInput from '@getaccept/dsr-shared-new/src/components/NameInput.vue';
import { useI18n } from 'vue-i18n';
import type { Participant } from '@getaccept/dsr-shared-new';
import { storeToRefs } from 'pinia';
import { useRevisionsStore } from '../store/revisions.store';
import SectionList from './SectionList.vue';

export default defineComponent({
  name: 'BlockListItem',
  components: {
    SectionList,
    NameInput,
  },
  props: {
    block: {
      type: Object as PropType<EditorBlock>,
      required: true,
    },
    isCurrentBlock: {
      type: Boolean,
      default: false,
    },
    participant: {
      type: Object as PropType<Participant>,
      default: null,
    },
    lockedForCc: {
      type: Boolean,
    },
  },
  setup(props, { emit }) {
    const { t } = useI18n();
    const sectionsOpen = ref(false);
    const nameInputValue = computed(() => props.block.content.displayName || t('untitled_page'));
    const viewed = ref(false);
    const revisionsStore = useRevisionsStore();
    const { latestViewedRevision } = storeToRefs(revisionsStore);

    const blockHasChanges = computed(() => {
      if (!latestViewedRevision.value) {
        return false;
      }

      return (
        !latestViewedRevision.value.pages.some(
          ({ editorBlockId }) => editorBlockId === props.block.id
        ) ||
        latestViewedRevision.value.pages.some(
          ({ editorBlockId, contentVersion }) =>
            editorBlockId === props.block.id && props.block.contentVersion !== contentVersion
        )
      );
    });

    watch(
      blockHasChanges,
      () => {
        viewed.value = !blockHasChanges.value;
      },
      { immediate: true }
    );

    watch(
      () => props.isCurrentBlock,
      () => {
        if (props.isCurrentBlock) {
          sectionsOpen.value = true;
          setTimeout(() => (viewed.value = true), 1000);
        }
      },
      { immediate: true }
    );

    const toggleSections = () => {
      sectionsOpen.value = !sectionsOpen.value;
    };

    const filteredSections = computed(() =>
      props.block.content.sections
        .filter(section => PlaceholderHelper.filterSectionForPlaceholders(section))
        .filter(section =>
          ContractHelper.filterSectionByContractLinkAccess(section, props.participant)
        )
    );

    const navigateToSection = event => {
      emit('navigate-to-section', event);
      setTimeout(() => (viewed.value = true), 1000);
    };

    const visibleSections = computed(() => filteredSections.value.filter(({ hidden }) => !hidden));

    return {
      sectionsOpen,
      toggleSections,
      nameInputValue,
      visibleSections,
      filteredSections,
      navigateToSection,
      blockHasChanges,
      viewed,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '@getaccept/dsr-shared-new/src/scss/block-list-item';

.circle {
  background: var(--info-dark);
  border-radius: 50%;
  height: 0.5rem;
  width: 0.5rem;
  margin-right: 0.4rem;
  margin-left: 0;
  transition: all 1s ease-out;
  flex-shrink: 0;
}
</style>
